import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const [activeHeading, setActiveHeading] = useState('');

  const headings = article?.content
    .filter((block) => block.type === 'h2' || block.type === 'h3')
    .map((block) => ({
      id: block.text.replace(/\s+/g, '-').toLowerCase(),
      text: block.text,
      level: block.type,
    })) || [];

  const relatedArticles = article
    ? articles.filter((a) => article.relatedArticleIds.includes(a.id))
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings
        .map((h) => document.getElementById(h.id))
        .filter(Boolean) as HTMLElement[];

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const el = headingElements[i];
        if (el.getBoundingClientRect().top <= 120) {
          setActiveHeading(headings[i].id);
          return;
        }
      }
      if (headingElements.length > 0) {
        setActiveHeading(headings[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || '分类', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '拍得好',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://piccraft.github.io/article/${article.slug}`,
    },
  };

  const faqSchema = article.faq
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a,
          },
        })),
      }
    : null;

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        publishedTime={article.date}
        modifiedTime={article.date}
        schema={faqSchema ? { ...articleSchema, ...faqSchema } : articleSchema}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb items={breadcrumbItems} />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                    {tag}
                  </span>
                ))}
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime} 分钟阅读</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex gap-10">
              <div className="flex-1 min-w-0">
                <div className="prose-content">
                  {article.content.map((block, index) => {
                    if (block.type === 'h2') {
                      return (
                        <h2
                          key={index}
                          id={block.text.replace(/\s+/g, '-').toLowerCase()}
                          className="text-xl font-bold text-foreground-950 font-heading mt-10 mb-4 pb-2 border-b border-background-200"
                        >
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === 'h3') {
                      return (
                        <h3
                          key={index}
                          id={block.text.replace(/\s+/g, '-').toLowerCase()}
                          className="text-lg font-semibold text-foreground-900 font-heading mt-8 mb-3"
                        >
                          {block.text}
                        </h3>
                      );
                    }
                    if (block.type === 'p') {
                      return (
                        <p key={index} className="text-sm text-foreground-700 leading-loose mb-4">
                          {block.text}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>

                {article.faq && article.faq.length > 0 && (
                  <section className="mt-12 p-6 bg-background-100 rounded-xl">
                    <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                      常见问题
                    </h2>
                    <div className="space-y-4">
                      {article.faq.map((item, index) => (
                        <div key={index}>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                            {item.q}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {item.a}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {headings.length > 0 && (
                <aside className="hidden lg:block w-56 flex-shrink-0">
                  <div className="sticky top-24">
                    <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                      目录
                    </h4>
                    <nav className="space-y-1 border-l-2 border-background-200">
                      {headings.map((heading) => (
                        <a
                          key={heading.id}
                          href={`#${heading.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(heading.id);
                            if (el) {
                              el.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                          className={`block text-xs py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                            heading.level === 'h3' ? 'pl-5' : 'pl-3'
                          } ${
                            activeHeading === heading.id
                              ? 'text-primary-600 border-l-2 border-primary-500 -ml-0.5 font-medium'
                              : 'text-foreground-500 hover:text-foreground-800 border-l-2 border-transparent -ml-0.5'
                          }`}
                        >
                          {heading.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                </aside>
              )}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200">
              <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{ra.readTime} 分钟阅读</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}